import {
  calendarMonthOf,
  periodCoversMonth,
  type OpenProjectAccountStatus,
} from "@payday/contracts";

export type PayrollProfilePeriod = {
  id: number;
  userId: number;
  validFromMonth: string;
  validToMonth: string;
  boundaryInferred?: boolean;
  contractType: string;
  contractualRate?: string | null;
  workloadPercent: number;
  hoursPerDay: number;
  annualHolidayEntitlement: number;
  calculationEnabled: boolean;
  changedAt?: string | null;
  reason?: string;
};

export type PayrollUser = {
  id: string;
  revision?: number;
  username: string;
  displayName: string;
  email: string;
  role: string;
  isActive: boolean;
  sourceAccountStatus?: OpenProjectAccountStatus | null;
  payrollEmployeeCode?: string;
  payrollEmail?: string;
  payrollPdfPasswordConfigured?: boolean;
  payrollCalculationEnabled?: boolean;
  payrollContractType?: string;
  payrollWorkloadPercent?: number;
  payrollStartDate?: string;
  payrollEndDate?: string;
  payrollHoursPerDay?: number;
  payrollAnnualHolidayEntitlement?: number;
  payrollVacationOpeningBalances?: VacationOpeningBalance[];
  payrollProfileNotes?: string;
  payrollEligibleForMonth?: boolean;
  payrollEligibilityMonth?: string;
  payrollEligibilityReason?:
    | "included"
    | "openproject_inactive"
    | "profile_missing"
    | "calculation_disabled"
    | "employment_dates_invalid"
    | "before_employment_start"
    | "after_employment_end"
    | "contract_period_missing"
    | "contract_period_disabled";
  profilePeriods?: PayrollProfilePeriod[];
};

export type VacationOpeningBalance = {
  id: number;
  year: number;
  effectiveMonth: string;
  appliesFromMonth: string;
  days: number;
  source: string;
  reason: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  revision: number;
};

export type PayrollProfileDraft = {
  expectedRevision?: number;
  payrollEmployeeCode: string;
  payrollEmail: string;
  payrollPdfPassword: string;
  payrollCalculationEnabled: boolean;
  payrollStartDate: string;
  payrollEndDate: string;
  payrollProfileNotes: string;
  payrollAnnualHolidayEntitlement: string;
  reason: string;
};
export type VacationOpeningBalanceDraft = {
  effectiveMonth: string;
  days: string;
  reason: string;
};

export type PayrollPeriodDraft = {
  expectedRevision?: number;
  validFromMonth: string;
  validToMonth: string;
  contractType: string;
  contractualRate?: string | null;
  workloadPercent: string;
  hoursPerDay: string;
  annualHolidayEntitlement: string;
  calculationEnabled: boolean;
  reason: string;
};

export type ProfileFilter =
  | "all"
  | "employees"
  | "freelancers"
  | "students"
  | "others";

export const PROFILE_DIRECTORY_PAGE_SIZE = 50;

export const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

export const dateValue = (value?: string) =>
  /^\d{4}-\d{2}$/.test(String(value ?? "")) ? `${value}-01` : (value ?? "");

export const profileFromUser = (user: PayrollUser): PayrollProfileDraft => ({
  ...(user.revision === undefined ? {} : { expectedRevision: user.revision }),
  payrollEmployeeCode: user.payrollEmployeeCode ?? "",
  payrollEmail: user.payrollEmail ?? "",
  payrollPdfPassword: "",
  payrollCalculationEnabled: user.payrollCalculationEnabled ?? true,
  payrollStartDate: dateValue(user.payrollStartDate),
  payrollEndDate: dateValue(user.payrollEndDate),
  payrollProfileNotes: user.payrollProfileNotes ?? "",
  payrollAnnualHolidayEntitlement: String(
    user.payrollAnnualHolidayEntitlement ?? 20,
  ),
  reason: "",
});
export const defaultVacationOpeningBalanceDraft = (
  user: PayrollUser,
  selectedMonth: string,
): VacationOpeningBalanceDraft => {
  const year = /^\d{4}-(0[1-9]|1[0-2])$/.test(selectedMonth)
    ? Number(selectedMonth.slice(0, 4))
    : new Date().getFullYear();
  const existing = user.payrollVacationOpeningBalances?.find(
    (balance) => balance.year === year,
  );
  return {
    effectiveMonth: existing?.effectiveMonth ?? `${year}-01`,
    days: existing ? String(existing.days) : "",
    reason: "",
  };
};

// Shape check for a contract/employment date (YYYY-MM-DD or legacy YYYY-MM).
// Month 01-12, optional two-digit day, fully anchored so a trailing
// non-numeric suffix is rejected (B3). The exact calendar-day validity is
// enforced by the domain/API, not here.
export const CONTRACT_DATE_RE = /^\d{4}-(0[1-9]|1[0-2])(-\d{2})?$/;

/** The YYYY-MM month immediately following `month` (12 -> next January). */
export const monthAfter = (month: string): string => {
  const year = Number(month.slice(0, 4));
  const monthNumber = Number(month.slice(5, 7));
  return monthNumber === 12
    ? `${year + 1}-01`
    : `${month.slice(0, 5)}${String(monthNumber + 1).padStart(2, "0")}`;
};

//
// Seeding for a brand-new contract period.
// - No existing periods: inherit the (derived) employment dates directly.
// - Existing periods: seed a period that does NOT overlap the current tail of
//   the timeline (migration 022 turns a legacy month-only through=2026-09 into
//   exact 2026-09-30, so inheriting `payrollEndDate` there would collide with
//   the last period and be rejected by `validatePeriod`/the API). The new
//   period starts the month after the latest closing period and is
//   open-ended (empty validToMonth). If any period is open-ended, a clean
//   non-overlapping successor cannot be derived, so dates are left for the
//   user to fill in.
//
export const defaultPeriodDraft = (user: PayrollUser): PayrollPeriodDraft => {
  const common = {
    ...(user.revision === undefined ? {} : { expectedRevision: user.revision }),
    contractType: user.payrollContractType ?? "employee",
    contractualRate: "",
    workloadPercent: String(
      user.payrollWorkloadPercent ??
        (user.payrollContractType === "student_agreement" ? 50 : 100),
    ),
    hoursPerDay: String(user.payrollHoursPerDay ?? 8),
    annualHolidayEntitlement: String(
      user.payrollAnnualHolidayEntitlement ?? 20,
    ),
    calculationEnabled: true,
    reason: "",
  };

  const periods = user.profilePeriods ?? [];
  if (periods.length === 0) {
    const employmentStartValid = CONTRACT_DATE_RE.test(
      user.payrollStartDate ?? "",
    );
    const employmentEndValid = CONTRACT_DATE_RE.test(user.payrollEndDate ?? "");
    return {
      validFromMonth: employmentStartValid
        ? user.payrollStartDate!
        : `${currentMonth()}-01`,
      validToMonth: employmentEndValid ? user.payrollEndDate! : "",
      ...common,
    };
  }

  const hasOpenEnded = periods.some((period) => !period.validToMonth);
  if (hasOpenEnded) {
    return { validFromMonth: "", validToMonth: "", ...common };
  }
  const latestClosingMonth = periods
    .map((period) => calendarMonthOf(period.validToMonth ?? ""))
    .filter(Boolean)
    .sort()
    .pop();
  return {
    validFromMonth: latestClosingMonth
      ? `${monthAfter(latestClosingMonth)}-01`
      : "",
    validToMonth: "",
    ...common,
  };
};

export const draftFromPeriod = (
  period: PayrollProfilePeriod,
  expectedRevision: number | undefined,
): PayrollPeriodDraft => ({
  ...(expectedRevision === undefined ? {} : { expectedRevision }),
  validFromMonth: period.validFromMonth,
  validToMonth: period.validToMonth,
  contractType: period.contractType,
  contractualRate: period.contractualRate ?? "",
  workloadPercent: String(period.workloadPercent),
  hoursPerDay: String(period.hoursPerDay),
  annualHolidayEntitlement: String(period.annualHolidayEntitlement),
  calculationEnabled: period.calculationEnabled,
  reason: "",
});

export const periodPayload = (draft: PayrollPeriodDraft) => ({
  expectedRevision: draft.expectedRevision,
  validFromMonth: draft.validFromMonth,
  validToMonth: draft.validToMonth || null,
  contractType: draft.contractType,
  contractualRate:
    draft.contractType === "other"
      ? null
      : draft.contractualRate?.trim().replace(",", ".") || null,
  workloadPercent: Number.parseFloat(draft.workloadPercent || "100"),
  hoursPerDay: Number.parseFloat(draft.hoursPerDay || "8"),
  annualHolidayEntitlement: Number.parseFloat(
    draft.annualHolidayEntitlement || "20",
  ),
  calculationEnabled: draft.calculationEnabled,
  reason: draft.reason.trim(),
});

export const employeeName = (user: PayrollUser) =>
  user.displayName || user.username;

export const sourceActivity = (user: PayrollUser) => {
  if (user.sourceAccountStatus === "active") return "active";
  if (
    user.sourceAccountStatus === "registered" ||
    user.sourceAccountStatus === "locked" ||
    user.sourceAccountStatus === "invited"
  )
    return "inactive";
  return "unknown";
};
export const sourceActivityOrder = { active: 0, unknown: 1, inactive: 2 };
export const sourceActivityLabel = {
  active: "Active",
  inactive: "Inactive",
  unknown: "Unknown",
};

export const payrollMonthLabel = (month?: string) => {
  if (!month || !/^\d{4}-(0[1-9]|1[0-2])$/.test(month))
    return "the current month";
  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric",
  }).format(new Date(`${month}-01T12:00:00`));
};

export type PayrollContextSearch = {
  month?: string;
  periodThrough?: string;
  resolve?: "missing-payslip";
};

export const payrollContextSearch = (
  principalId?: string,
): PayrollContextSearch => {
  if (typeof window === "undefined") return {};
  const incoming = new URLSearchParams(window.location.search);
  const historyState: unknown = window.history.state;
  const retained = (
    historyState &&
    typeof historyState === "object" &&
    "payrollProfileRecovery" in historyState
      ? historyState.payrollProfileRecovery
      : undefined
  ) as PayrollContextSearch | undefined;
  let search = incoming.has("month")
    ? incoming
    : new URLSearchParams(retained ? Object.entries(retained) : []);
  if (
    !search.has("month") &&
    principalId &&
    incoming.get("resolve") === "missing-payslip"
  ) {
    try {
      const stored = new URLSearchParams(
        window.sessionStorage.getItem(`payroll-context:${principalId}`) ?? "",
      );
      if (stored.get("principal") === principalId) search = stored;
    } catch {
      // Recovery still opens if browser session storage is unavailable.
    }
  }
  const month = search.get("month");
  const periodThrough = search.get("periodThrough");
  const resolve = incoming.get("resolve") ?? search.get("resolve");
  const normalizedMonth =
    month && /^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? month : undefined;
  const normalizedPeriodThrough =
    periodThrough && /^\d{4}-(0[1-9]|1[0-2])$/.test(periodThrough)
      ? periodThrough
      : undefined;
  const normalizedResolve = resolve === "missing-payslip" ? resolve : undefined;
  return {
    ...(normalizedMonth ? { month: normalizedMonth } : {}),
    ...(normalizedPeriodThrough
      ? { periodThrough: normalizedPeriodThrough }
      : {}),
    ...(normalizedResolve ? { resolve: normalizedResolve } : {}),
  };
};

export const directoryPeriod = (user: PayrollUser) =>
  [...(user.profilePeriods ?? [])].sort(
    (a, b) => b.validFromMonth.localeCompare(a.validFromMonth) || b.id - a.id,
  )[0];

export const directoryType = (user: PayrollUser) =>
  directoryPeriod(user)?.contractType ?? user.payrollContractType;

export const applicableAllowancePeriod = (user: PayrollUser) =>
  [...(user.profilePeriods ?? [])]
    .filter((period) =>
      periodCoversMonth(
        period.validFromMonth,
        period.validToMonth,
        user.payrollEligibilityMonth ?? currentMonth(),
      ),
    )
    .sort(
      (a, b) => b.validFromMonth.localeCompare(a.validFromMonth) || b.id - a.id,
    )[0];

export const periodSubtitle = (user: PayrollUser) => {
  const period = directoryPeriod(user);
  return period
    ? `${period.validFromMonth} – ${period.validToMonth || "ongoing"}`
    : "No contract periods";
};

export const rateLabels: Record<string, string> = {
  employee: "Monthly gross salary (EUR)",
  student_agreement: "Gross hourly rate (EUR/hour)",
  freelancer: "Hourly rate excluding VAT (EUR/hour)",
};

export const contractLabel = (value: string) => {
  if (value === "student_agreement") return "Student agreement";
  if (value === "employee") return "Employee";
  if (value === "freelancer") return "Freelancer";
  return "Other";
};

export const formatChangedAt = (value?: string | null) => {
  if (!value) return "Change time unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

export const auditReasonError = (value: string) => {
  const length = value.trim().length;
  if (length < 15) return "Audit reason must be at least 15 characters.";
  if (length > 500) return "Audit reason cannot exceed 500 characters.";
  return "";
};

export const auditReasonHint = (value: string) =>
  `${value.trim().length}/500 characters · optional; a clear change summary is recorded automatically`;

export const errorMessage = (error: unknown) => {
  if (typeof error === "object" && error !== null && "response" in error) {
    const response = (error as { response?: { data?: { error?: unknown } } })
      .response;
    if (typeof response?.data?.error === "string") return response.data.error;
  }
  return error instanceof Error
    ? error.message
    : "The payroll profile change could not be saved.";
};

export type OperationDiagnostic = {
  occurredAt: string;
  operation: string;
  statusCode?: number;
  code?: string;
  correlationId?: string;
  retryable: boolean;
};

export type ProfileFeedback = {
  tone: "success" | "error";
  message: string;
  diagnostic?: OperationDiagnostic;
};

export const diagnosticFromError = (
  operation: string,
  error: unknown,
): OperationDiagnostic => {
  const detail =
    typeof error === "object" && error !== null
      ? (error as {
          status?: unknown;
          code?: unknown;
          correlationId?: unknown;
          retryable?: unknown;
        })
      : {};
  return {
    occurredAt: new Date().toISOString(),
    operation,
    ...(typeof detail.status === "number" ? { statusCode: detail.status } : {}),
    ...(typeof detail.code === "string" ? { code: detail.code } : {}),
    ...(typeof detail.correlationId === "string"
      ? { correlationId: detail.correlationId }
      : {}),
    retryable: detail.retryable === true,
  };
};

export const validateProfile = (draft: PayrollProfileDraft) => {
  if (
    draft.payrollEmail &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.payrollEmail.trim())
  )
    return "Enter a valid payroll email address.";
  if (draft.payrollPdfPassword && draft.payrollPdfPassword.length < 8)
    return "The PDF password must contain at least 8 characters.";
  if (
    draft.payrollStartDate &&
    !/^\d{4}-\d{2}-\d{2}$/.test(draft.payrollStartDate)
  )
    return "Employment start date must be a valid date.";
  if (draft.payrollEndDate && !/^\d{4}-\d{2}-\d{2}$/.test(draft.payrollEndDate))
    return "Employment end date must be a valid date.";
  if (
    draft.payrollStartDate &&
    draft.payrollEndDate &&
    draft.payrollEndDate < draft.payrollStartDate
  ) {
    return "Employment end date cannot be before the start date.";
  }
  const entitlement = Number.parseFloat(draft.payrollAnnualHolidayEntitlement);
  if (!Number.isFinite(entitlement) || entitlement < 0)
    return "Vacation entitlement cannot be negative.";
  return "";
};
export const validateVacationOpeningBalance = (
  draft: VacationOpeningBalanceDraft,
) => {
  if (!/^\d{4}-01$/.test(draft.effectiveMonth))
    return "Opening vacation balance must take effect in January.";
  const days = Number.parseFloat(draft.days);
  if (!Number.isFinite(days) || days < 0)
    return "Opening vacation balance must be a non-negative number of days.";
  return auditReasonError(draft.reason) || "";
};

export const validatePeriod = (
  draft: PayrollPeriodDraft,
  requiresChangedFields = false,
  changed = true,
) => {
  const workload = Number.parseFloat(draft.workloadPercent);
  const hours = Number.parseFloat(draft.hoursPerDay);
  const entitlement = Number.parseFloat(draft.annualHolidayEntitlement);
  if (!draft.validFromMonth) return "A start date is required.";
  if (draft.validToMonth && draft.validToMonth < draft.validFromMonth)
    return "The end date cannot be before the start date.";
  if (!Number.isFinite(workload) || workload <= 0 || workload > 100)
    return "Workload must be between 0 and 100 percent.";
  if (!Number.isFinite(hours) || hours <= 0)
    return "Hours per day must be greater than zero.";
  if (!Number.isFinite(entitlement) || entitlement < 0)
    return "Holiday entitlement cannot be negative.";
  if (requiresChangedFields && !changed)
    return "Change at least one period field before saving.";
  return "";
};
